import React from 'react';
import { useTranslation } from 'react-i18next';
import { BuildingIcon, UsersIcon } from '@hero/web-icons';

export type LocationType = 'own' | 'client';

export interface LocationTypeSelectorProps {
  value: LocationType;
  onChange: (value: LocationType) => void;
  /** When true, the buttons are non-interactive (used with forceType). */
  disabled?: boolean;
}

export function LocationTypeSelector({
  value,
  onChange,
  disabled = false,
}: LocationTypeSelectorProps) {
  const { t } = useTranslation('m-location');

  return (
    <div className="mb-6">
      <h3 className="text-sm font-medium text-gray-700 mb-3 text-start">
        {t('form.location_type_field.title')}
      </h3>
      <div className="grid grid-cols-2 gap-4">
        {/* My Location Option */}
        <button
          type="button"
          onClick={() => !disabled && onChange('own')}
          disabled={disabled}
          aria-pressed={value === 'own'}
          className={`p-4 rounded-lg border-2 transition-all text-start ${
            value === 'own'
              ? 'border-primary bg-primary/5'
              : 'border-gray-200 hover:border-gray-300'
          } ${disabled ? 'opacity-60 cursor-not-allowed' : ''}`}>
          <div className="flex items-center gap-2 mb-2 rtl:flex-row-reverse">
            <BuildingIcon
              className={`w-5 h-5 ${value === 'own' ? 'text-primary' : 'text-gray-500'}`}
            />
            <h3
              className={`font-semibold text-start ${
                value === 'own' ? 'text-primary' : 'text-gray-700'
              }`}>
              {t('location_type.my_location.title')}
            </h3>
          </div>
          <p className="text-xs text-gray-600 text-start">
            {t('location_type.my_location.form_description')}
          </p>
        </button>

        {/* For a Client Option */}
        <button
          type="button"
          onClick={() => !disabled && onChange('client')}
          disabled={disabled}
          aria-pressed={value === 'client'}
          className={`p-4 rounded-lg border-2 transition-all text-start ${
            value === 'client'
              ? 'border-primary bg-primary/5'
              : 'border-gray-200 hover:border-gray-300'
          } ${disabled ? 'opacity-60 cursor-not-allowed' : ''}`}>
          <div className="flex items-center gap-2 mb-2 rtl:flex-row-reverse">
            <UsersIcon
              className={`w-5 h-5 ${value === 'client' ? 'text-primary' : 'text-gray-500'}`}
            />
            <h3
              className={`font-semibold text-start ${
                value === 'client' ? 'text-primary' : 'text-gray-700'
              }`}>
              {t('location_type.for_client.title')}
            </h3>
          </div>
          <p className="text-xs text-gray-600 text-start">
            {t('location_type.for_client.form_description')}
          </p>
        </button>
      </div>
    </div>
  );
}
